/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from "@/integrations/supabase/client";
import { Tables, TablesInsert } from "@/types/database.types";

/**
 * IMPORTANT NOTE: There is a mismatch between the TypeScript types and the actual database schema.
 *
 * The TypeScript types expect a 'role' field in the messages table, but the actual database
 * uses a 'sender_type' field which is an enum with values: "ai", "user", or "agent_chat_user".
 *
 * To work around this, we use type assertions (as any) when inserting data into the database.
 * This allows us to use the correct field names that match the actual database schema.
 *
 * When converting between UI and database formats:
 * - UI "user" -> Database "user"
 * - UI "agent" -> Database "ai"
 * - UI "system" -> Database "ai" with is_system_message in metadata
 */

// This defines what the message type is according to TypeScript definitions
type Message = Tables<"messages">;
type MessageInsert = TablesInsert<"messages">;

// Define the actual database schema structure based on real inspection
type MessageActual = {
  id: string;
  content: string;
  conversation_id: string;
  created_at: string;
  metadata: any;
  sender_type: "user" | "ai" | "agent_chat_user"; // This is what the DB actually uses
  sender_id?: string | null;
  openai_message_id?: string | null;
};

// Create a UI message type for internal use
type UIMessage = {
  type: "user" | "agent" | "system";
  content: string;
  timestamp: string;
  id?: string;
};

/**
 * Saves a user message to the database
 */
export const saveUserMessage = async (
  conversationId: string,
  content: string,
  senderId?: string
) => {
  try {
    console.log(
      `[DB SAVE] Saving user message to conversation ${conversationId}: "${content.substring(
        0,
        50
      )}${content.length > 50 ? "..." : ""}"`
    );

    const { data, error } = await supabase
      .from("messages")
      .insert({
        conversation_id: conversationId,
        content,
        sender_type: "user",
        sender_id: senderId || null,
      } as any)
      .select("*")
      .single();

    if (error) {
      console.error("[DB SAVE ERROR] Failed to save user message:", error);
      return { error };
    }

    console.log(`[DB SAVE SUCCESS] User message saved with ID: ${data.id}`);

    // Update the conversation's last activity timestamp
    await updateConversationTimestamp(conversationId);

    return { data };
  } catch (error: unknown) {
    console.error("[DB SAVE ERROR] Error saving user message:", error);
    return { error };
  }
};

/**
 * Saves an assistant (AI) message to the database
 */
export const saveAssistantMessage = async (
  conversationId: string,
  content: string,
  openaiMessageId?: string,
  metadata?: Record<string, unknown>
) => {
  try {
    console.log(
      `[DB SAVE] Saving assistant message to conversation ${conversationId}: "${content.substring(
        0,
        50
      )}${content.length > 50 ? "..." : ""}"`
    );

    const { data, error } = await supabase
      .from("messages")
      .insert({
        conversation_id: conversationId,
        content,
        sender_type: "ai",
        openai_message_id: openaiMessageId || null,
        metadata: metadata || null,
      } as any)
      .select("*")
      .single();

    if (error) {
      console.error("[DB SAVE ERROR] Failed to save assistant message:", error);
      return { error };
    }

    console.log(
      `[DB SAVE SUCCESS] Assistant message saved with ID: ${data.id}`
    );

    // Update the conversation's last activity timestamp
    await updateConversationTimestamp(conversationId);

    return { data };
  } catch (error: unknown) {
    console.error("[DB SAVE ERROR] Error saving assistant message:", error);
    return { error };
  }
};

/**
 * Saves a system message to the database
 */
export const saveSystemMessage = async (
  conversationId: string,
  content: string
) => {
  try {
    console.log(
      `[DB SAVE] Saving system message to conversation ${conversationId}: "${content.substring(
        0,
        50
      )}${content.length > 50 ? "..." : ""}"`
    );

    // System messages are treated as AI messages with special metadata
    const { data, error } = await supabase
      .from("messages")
      .insert({
        conversation_id: conversationId,
        content,
        sender_type: "ai",
        metadata: { is_system_message: true },
      } as any)
      .select("*")
      .single();

    if (error) {
      console.error("[DB SAVE ERROR] Failed to save system message:", error);
      return { error };
    }

    console.log(`[DB SAVE SUCCESS] System message saved with ID: ${data.id}`);

    return { data };
  } catch (error: unknown) {
    console.error("[DB SAVE ERROR] Error saving system message:", error);
    return { error };
  }
};

/**
 * Saves messages in bulk after a conversation is closed
 */
export const saveMessagesInBulk = async (
  conversationId: string,
  messages: Array<{
    content: string;
    sender_type?: string;
    role?: string;
    sender_id?: string | null;
    openai_message_id?: string | null;
    metadata?: Record<string, unknown> | null;
  }>
) => {
  try {
    if (!messages.length) {
      return { data: [], error: null };
    }

    console.log(
      `[DB SAVE] Saving ${messages.length} messages in bulk to conversation ${conversationId}`
    );

    // Format messages for insert with correct database fields
    const messagesToInsert = messages.map((msg) => {
      // Map UI role values to database sender_type values if needed
      let senderType = msg.sender_type;
      let messageMetadata = msg.metadata || null;

      // If we have role but no sender_type, map it
      if (!senderType && msg.role) {
        if (msg.role === "user") {
          senderType = "user";
        } else if (msg.role === "assistant" || msg.role === "agent") {
          senderType = "ai";
        } else if (msg.role === "system") {
          senderType = "ai";
          messageMetadata = { ...messageMetadata, is_system_message: true };
        } else {
          senderType = "ai"; // Default to AI for unknown roles
        }
      }

      return {
        conversation_id: conversationId,
        content: msg.content,
        sender_type: senderType,
        sender_id: msg.sender_id || null,
        openai_message_id: msg.openai_message_id || null,
        metadata: messageMetadata,
      };
    });

    const { data, error } = await supabase
      .from("messages")
      .insert(messagesToInsert as any)
      .select("*");

    if (error) {
      console.error("[DB SAVE ERROR] Failed to save messages in bulk:", error);
      return { error };
    }

    console.log(
      `[DB SAVE SUCCESS] Saved ${data.length} messages in bulk to conversation ${conversationId}`
    );

    // Update the conversation's last activity timestamp
    await updateConversationTimestamp(conversationId);

    return { data };
  } catch (error: unknown) {
    console.error("[DB SAVE ERROR] Error saving messages in bulk:", error);
    return { error };
  }
};

/**
 * Fetches all messages for a conversation
 */
export const fetchConversationMessages = async (conversationId: string) => {
  try {
    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching conversation messages:", error);
      return { error };
    }

    return { data };
  } catch (error: unknown) {
    console.error("Error fetching conversation messages:", error);
    return { error };
  }
};

/**
 * Processes and saves UI chat messages when closing a conversation
 * This converts ChatMessage format to the database format and saves them
 */
export const saveConversationMessagesOnClose = async (
  conversationId: string,
  chatMessages: Array<{
    type: "user" | "agent" | "system";
    content: string;
    timestamp: string;
    id?: string;
  }>
) => {
  try {
    if (!chatMessages.length) {
      console.log("No messages to save for conversation:", conversationId);
      return { success: true };
    }

    console.log(
      `[DB SAVE] Saving ${chatMessages.length} messages on conversation close for ID ${conversationId}`
    );

    // Convert UI message format to database format
    const messagesToSave = chatMessages.map((msg) => {
      // Map UI message types to database format
      let senderType: "user" | "ai" | "agent_chat_user";
      let metadata: Record<string, unknown> | null = null;

      if (msg.type === "user") {
        senderType = "user";
      } else if (msg.type === "agent") {
        senderType = "ai";
      } else if (msg.type === "system") {
        senderType = "ai";
        metadata = { is_system_message: true };
      } else {
        // Default fallback
        senderType = "ai";
      }

      return {
        conversation_id: conversationId,
        content: msg.content,
        sender_type: senderType,
        metadata,
      };
    });

    // Save the messages to the database
    const { data, error } = await supabase
      .from("messages")
      .insert(messagesToSave as any)
      .select("*");

    if (error) {
      console.error(
        "[DB SAVE ERROR] Failed to save conversation messages on close:",
        error
      );
      return { error };
    }

    console.log(
      `[DB SAVE SUCCESS] Successfully saved ${messagesToSave.length} messages on conversation close`
    );

    return { success: true, data };
  } catch (error: unknown) {
    console.error(
      "[DB SAVE ERROR] Error in saveConversationMessagesOnClose:",
      error
    );
    return { error };
  }
};

/**
 * Updates a conversation's timestamp to mark it as recently active
 */
const updateConversationTimestamp = async (conversationId: string) => {
  try {
    const { error } = await supabase
      .from("conversations")
      .update({
        updated_at: new Date().toISOString(),
      })
      .eq("id", conversationId);

    if (error) {
      console.error("Error updating conversation timestamp:", error);
    }
  } catch (error) {
    console.error("Error updating conversation timestamp:", error);
  }
};

/**
 * Updates a conversation's state (open/completed)
 */
export const updateConversationState = async (
  conversationId: string,
  state: "open" | "completed"
) => {
  try {
    const { data, error } = await supabase
      .from("conversations")
      .update({
        state,
        updated_at: new Date().toISOString(),
      })
      .eq("id", conversationId)
      .select("*")
      .single();

    if (error) {
      console.error("Error updating conversation state:", error);
      return { error };
    }

    // NOTE: Notifications are now handled by extract-form-fields function
    // which calls notifications-post-chat-completion after all data is processed.
    // This ensures emails/webhooks contain complete extracted data and summaries.

    return { data };
  } catch (error: unknown) {
    console.error("Error updating conversation state:", error);
    return { error };
  }
};

/**
 * Creates a new conversation
 */
export const createConversation = async (
  agentId: string,
  userId?: string,
  agentChatUserId?: string
) => {
  try {
    console.log(`[DB CREATE] Creating new conversation for agent ${agentId}`);

    const { data, error } = await supabase
      .from("conversations")
      .insert({
        agent_id: agentId,
        user_id: userId || null,
        agent_chat_user_id: agentChatUserId || null,
        state: "open",
      })
      .select("*")
      .single();

    if (error) {
      console.error("[DB CREATE ERROR] Failed to create conversation:", error);
      return { error };
    }

    console.log(
      `[DB CREATE SUCCESS] Created new conversation with ID: ${data.id}`
    );

    return { data };
  } catch (error: unknown) {
    console.error("[DB CREATE ERROR] Error creating conversation:", error);
    return { error };
  }
};
